import { activateTab } from './tabs';
import { normalizeTabHash } from './tabHash';

/**
 * Deep-Links auf die Tabs der Produktdetailseite: /…/product/123#kundeninformationen öffnet den Tab,
 * dessen data-tab-anchor passt. Die Anker erzeugt der Server (ProductTabAnchors.cs) aus dem Tab-Label.
 */

// Bewusst kein querySelector mit dem Hash: der ist Benutzereingabe und müsste sonst escaped werden.
function findTabByAnchor(anchor: string): HTMLElement | null {
    const tabs = Array.from(document.querySelectorAll<HTMLElement>('.tab-list .tab'));
    return tabs.find(tab => tab.dataset.tabAnchor === anchor) ?? null;
}

// Absolute Zielposition statt scrollIntoView, damit mehrfaches Aufrufen (DOM-Ready + load) idempotent ist.
// Der Abstand nach oben kommt aus scroll-margin-top im SCSS.
function scrollToTabs(behavior: ScrollBehavior) {
    const tabs = document.querySelector<HTMLElement>('.product-info-tabs');
    if (!tabs) return;

    const margin = parseFloat(getComputedStyle(tabs).scrollMarginTop) || 0;
    const top = tabs.getBoundingClientRect().top + window.scrollY - margin;
    window.scrollTo({ top: Math.max(0, top), behavior });
}

/** Aktiviert den Tab zum aktuellen Hash. Liefert false, wenn kein Tab passt. */
function openTabFromHash(): boolean {
    const anchor = normalizeTabHash(window.location.hash);
    const tab = anchor ? findTabByAnchor(anchor) : null;
    if (!tab) return false;

    activateTab(tab);
    return true;
}

// Bei Reload und Zurück/Vor stellt der Browser die Scrollposition selbst wieder her. Der Anker bleibt
// nach einem Deep-Link in der URL, sonst würde jedes F5 danach erneut zur Tab-Leiste springen.
function isScrollRestoringNavigation(): boolean {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return navigation?.type === 'reload' || navigation?.type === 'back_forward';
}

export function initTabDeepLink() {
    if (!document.querySelector('.product-info-tabs')) return;

    // Den Tab auch bei Reload öffnen, nur das Scrollen dem Browser überlassen.
    if (openTabFromHash() && !isScrollRestoringNavigation()) {
        scrollToTabs('auto');

        // Bilder und Slider oberhalb der Tabs verschieben das Layout beim Nachladen noch – nachscrollen,
        // sofern der Nutzer bis dahin nicht selbst gescrollt hat.
        if (document.readyState !== 'complete') {
            let userScrolled = false;
            const markUserScrolled = () => (userScrolled = true);
            ['wheel', 'touchstart', 'keydown'].forEach(type =>
                window.addEventListener(type, markUserScrolled, { once: true, passive: true })
            );

            window.addEventListener(
                'load',
                () => {
                    if (!userScrolled) scrollToTabs('auto');
                },
                { once: true }
            );
        }
    }

    // Hash in der Adressleiste geändert oder In-Page-Link wie <a href="#rezensionen"> geklickt.
    window.addEventListener('hashchange', () => {
        if (openTabFromHash()) {
            scrollToTabs('smooth');
        }
    });
}
